import React, { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { getApiErrorMessage } from '../utils/errorUtils';

// ZoomCharts node/link types
interface ZoomChartsNode {
  id: string;
  name: string;
  type: string;
  count?: number;
  health_status?: string;
  is_preferred?: boolean;
  supplier_id?: number;
  [key: string]: unknown;
}

interface ZoomChartsLink {
  id: string;
  from: string;
  to: string;
  [key: string]: unknown;
}

interface ZoomChartsNodeStyle {
  data: ZoomChartsNode;
  fillColor?: string;
  radius?: number;
  label?: string;
  labelStyle?: { fillColor?: string; font?: string };
  items?: Array<{ text: string }>;
}

interface ZoomChartsLinkStyle {
  fillColor?: string;
  toDecoration?: string;
}

interface ZoomChartsClickEvent {
  clickNode?: { data: ZoomChartsNode };
}

type ZoomChartsConstructor = new (config: Record<string, unknown>) => { remove: () => void; addData: (data: unknown) => void; replaceData?: (data: unknown) => void };

// TypeScript declarations for ZoomCharts
declare global {
  interface Window {
    NetChart: ZoomChartsConstructor;
    PieChart: ZoomChartsConstructor;
  }
}

interface GraphCategory {
  nodes: ZoomChartsNode[];
  links: ZoomChartsLink[];
}

interface GraphData {
  hub: GraphCategory;
  capabilities: GraphCategory;
  products: GraphCategory;
  regions: GraphCategory;
}

const SupplierIntelligence: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const netChartRef = useRef<{ remove: () => void; addData: (data: unknown) => void } | null>(null);
  const pieChartRef = useRef<{ remove: () => void; replaceData?: (data: unknown) => void } | null>(null);

  // Load ZoomCharts library
  useEffect(() => {
    // Check if ZoomCharts is already loaded
    if (window.NetChart) {
      loadGraphData();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="/js/zoomcharts-1.16.5/zoomcharts.js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        loadGraphData();
      });
      return;
    }

    const script = document.createElement('script');
    script.src = '/js/zoomcharts-1.16.5/zoomcharts.js';
    script.async = true;
    script.onload = () => {
      loadGraphData();
    };
    script.onerror = (error) => {
      console.error('Failed to load ZoomCharts:', error);
      setError('Failed to load ZoomCharts library. Please check that the file exists.');
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      if (netChartRef.current) {
        try {
          netChartRef.current.remove();
        } catch (e) {
          console.warn('Error cleaning up NetChart:', e);
        }
      }
      if (pieChartRef.current) {
        try {
          pieChartRef.current.remove();
        } catch (e) {
          console.warn('Error cleaning up PieChart:', e);
        }
      }
      // Don't remove script to avoid issues with re-mounting
    };
  }, []);

  const loadGraphData = async () => {
    try {
      const response = await api.get('/supplier-intelligence/graph-data/');
      setGraphData(response.data);
      initializeChart(response.data);
    } catch (err: unknown) {
      console.error('Failed to load graph data:', err);
      setError(getApiErrorMessage(err, 'Failed to load graph data'));
    } finally {
      setLoading(false);
    }
  };

  const initializeChart = (data: GraphData) => {
    if (!chartContainerRef.current) {
      console.error('Chart container ref is null');
      return;
    }

    if (!window.NetChart) {
      console.error('ZoomCharts NetChart not available');
      return;
    }

    // Clear existing chart
    if (netChartRef.current) {
      try {
        netChartRef.current.remove();
      } catch (e) {
        console.warn('Error removing old chart:', e);
      }
    }

    // Initialize with hub data
    const initialData = {
      preloaded: {
        nodes: data.hub.nodes,
        links: data.hub.links
      }
    };

    try {
      // Create NetChart
      netChartRef.current = new window.NetChart({
        container: chartContainerRef.current,
        area: { height: 800 },
        data: initialData,
        style: {
          nodeStyleFunction: (node: ZoomChartsNodeStyle) => {
            const nodeData = node.data;

            // Hub styling
            if (nodeData.type === 'hub') {
              node.fillColor = '#014F70';
              node.radius = 50;
              node.label = nodeData.name;
              node.labelStyle = {
                fillColor: '#FFFFFF',
                font: '16px Arial, sans-serif'
              };
            }

            // Category styling
            else if (nodeData.type === 'category') {
              node.fillColor = '#0891B2';
              node.radius = 40;
              node.label = nodeData.name;
              node.labelStyle = {
                fillColor: '#FFFFFF',
                font: '14px Arial, sans-serif'
              };
            }

            // Subcategory styling
            else if (nodeData.type === 'subcategory') {
              node.fillColor = '#06B6D4';
              node.radius = 30;
              node.label = nodeData.name;
              node.labelStyle = {
                fillColor: '#000000',
                font: '12px Arial, sans-serif'
              };
              // Add count badge
              if (nodeData.count != null && nodeData.count > 0) {
                node.items = [{ text: nodeData.count.toString() }];
              }
            }

            // Supplier styling
            else if (nodeData.type === 'supplier') {
              node.radius = 25;
              node.label = nodeData.name;

              // Color by health status
              if (nodeData.health_status === 'critical') {
                node.fillColor = '#EF4444'; // red
              } else if (nodeData.health_status === 'watch') {
                node.fillColor = '#F59E0B'; // yellow
              } else if (nodeData.health_status === 'unrated') {
                node.fillColor = '#9CA3AF'; // grey: no rating yet (CR-7714), not "healthy"
              } else {
                node.fillColor = '#10B981'; // green
              }

              node.labelStyle = {
                fillColor: '#000000',
                font: '11px Arial, sans-serif'
              };

              // Add strategic supplier star
              if (nodeData.is_preferred) {
                node.items = [{ text: '⭐' }];
              }
            }
          },
          linkStyleFunction: (link: ZoomChartsLinkStyle) => {
            link.fillColor = '#94A3B8';
            link.toDecoration = 'arrow';
          }
        },
        events: {
          onClick: (event: ZoomChartsClickEvent) => {
            if (event.clickNode) {
              handleNodeClick(event.clickNode, data);
            }
          }
        },
        navigation: {
          initialNodes: data.hub.nodes.map((n: ZoomChartsNode) => n.id)
        }
      });

      // Initialize PieChart for overlay
      if (window.PieChart) {
        pieChartRef.current = new window.PieChart({
          parentChart: netChartRef.current,
          data: { preloaded: { subvalues: [] } }
        });
      }
    } catch (error) {
      console.error('Error initializing chart:', error);
      setError('Failed to initialize visualization. Please refresh the page.');
    }
  };

  const handleNodeClick = async (node: { data: ZoomChartsNode }, data: GraphData) => {
    const nodeData = node.data;

    // Category click - expand to subcategories
    if (nodeData.type === 'category') {
      const categoryData = data[nodeData.id as keyof typeof data];
      if (categoryData && 'nodes' in categoryData && 'links' in categoryData) {
        expandCategory(nodeData.id, categoryData);
      }
    }

    // Supplier click - show PieChart stats
    else if (nodeData.type === 'supplier' && nodeData.supplier_id != null) {
      await showSupplierStats(nodeData.supplier_id);
    }
  };

  const expandCategory = (_categoryId: string, categoryData: GraphCategory) => {
    if (!netChartRef.current) return;

    // Add category nodes and links to the chart using addData
    try {
      netChartRef.current.addData({
        preloaded: {
          nodes: categoryData.nodes,
          links: categoryData.links
        }
      });
    } catch (error) {
      console.error('Error expanding category:', error);
    }
  };

  const showSupplierStats = async (supplierId: number) => {
    if (!pieChartRef.current) return;

    try {
      const response = await api.get(`/supplier-intelligence/supplier-stats/${supplierId}/`);
      const pieData = response.data;

      pieChartRef.current.replaceData?.({
        preloaded: {
          subvalues: pieData.subvalues
        }
      });
    } catch (err) {
      console.error('Failed to load supplier stats:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading Supplier Intelligence...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white dark:bg-gray-900 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error Loading Supplier Intelligence</h3>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Supplier Intelligence
        </h1>
        <p className="text-gray-600 mt-2">
          Visualize your supply chain relationships and identify risks and opportunities.
        </p>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-4 bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-green-500" />
          <span className="text-sm text-gray-700">Healthy Supplier (Rating ≥ 4.0)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-yellow-500" />
          <span className="text-sm text-gray-700">Watch (Rating 3.0-3.9)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-red-500" />
          <span className="text-sm text-gray-700">Critical (Rating &lt; 3.0)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gray-400" />
          <span className="text-sm text-gray-700">Unrated (no rating yet)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">⭐ Strategic Supplier</span>
        </div>
      </div>

      {/* Debug Info - only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Debug Info:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>ZoomCharts Loaded: {window.NetChart ? '✓ Yes' : '✗ No'}</li>
            <li>Data Loaded: {graphData ? '✓ Yes' : '✗ No'}</li>
            <li>Hub Nodes: {graphData?.hub?.nodes?.length || 0}</li>
            <li>Chart Initialized: {netChartRef.current ? '✓ Yes' : '✗ No'}</li>
          </ul>
        </div>
      )}

      {/* Chart Container */}
      <div
        ref={chartContainerRef}
        className="w-full bg-white rounded-lg shadow-lg border-4 border-primary/30"
        style={{ height: '800px', minHeight: '800px' }}
      >
        {!graphData && (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>Chart container ready - waiting for data...</p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4 shadow">
        <h3 className="font-semibold text-gray-900">How to use:</h3>
        <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
          <li>Click on a category (Capabilities, Products/Parts, Regions) to expand and see subcategories</li>
          <li>Click on subcategories to see suppliers in that group</li>
          <li>Click on a supplier node to see detailed statistics</li>
          <li>Use mouse wheel to zoom in/out, drag to pan</li>
          <li>Node colors indicate supplier health: green (healthy), yellow (watch), red (critical), grey (no rating yet)</li>
        </ul>
      </div>
    </div>
  );
};

export default SupplierIntelligence;
